import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { useTheme } from "@/components/ThemeContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxury-ride-avellino/artifacts/v2iwkqbg_N.C.C.svg";

const serviceLinks = [
  { label: "Transfer per Aeroporti", path: "/servizi", hash: "service-detail-0" },
  { label: "Transfer per Stazioni", path: "/servizi", hash: "service-detail-1" },
  { label: "Business Travel", path: "/servizi", hash: "service-detail-2" },
  { label: "Eventi e Cerimonie", path: "/servizi", hash: "service-detail-3" },
  { label: "Tour Personalizzati", path: "/servizi", hash: "service-detail-4" },
];

function PaymentIcon({ name, children }) {
  return (
    <div
      data-testid={`payment-icon-${name}`}
      className="w-12 h-8 rounded flex items-center justify-center bg-white overflow-hidden"
      title={name}
    >
      {children}
    </div>
  );
}

function PaymentIcons() {
  return (
    <div data-testid="payment-methods" className="flex flex-wrap items-center gap-2">
      {/* Visa */}
      <PaymentIcon name="visa">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="white" rx="2"/>
          <path d="M19.5 21h-3l1.9-11.5h3L19.5 21zm12.8-11.2c-.6-.2-1.5-.5-2.7-.5-3 0-5.1 1.6-5.1 3.8 0 1.7 1.5 2.6 2.6 3.1 1.1.6 1.5.9 1.5 1.4 0 .8-.9 1.1-1.7 1.1-1.2 0-1.8-.2-2.7-.6l-.4-.2-.4 2.5c.7.3 2 .6 3.3.6 3.2 0 5.2-1.6 5.3-3.9 0-1.3-.8-2.3-2.5-3.1-1-.5-1.7-.9-1.7-1.4 0-.5.5-1 1.7-1 1 0 1.7.2 2.2.4l.3.1.3-2.3zM37 9.5h-2.3c-.7 0-1.3.2-1.6 1L29 21h3.2l.6-1.8h3.9l.4 1.8H40L37 9.5zm-3.7 8.2c.3-.7 1.2-3.4 1.2-3.4l.4-1 .2.9.7 3.5h-2.5zM16 9.5l-2.8 7.9-.3-1.5c-.5-1.8-2.2-3.7-4-4.7l2.7 9.8h3.2l4.8-11.5H16z" fill="#1A1F71"/>
          <path d="M11.1 9.5H6l-.1.3c3.8 1 6.3 3.3 7.3 6.1l-1.1-5.4c-.2-.8-.7-1-1-1z" fill="#F9A533"/>
        </svg>
      </PaymentIcon>

      {/* Mastercard */}
      <PaymentIcon name="mastercard">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="white" rx="2"/>
          <circle cx="19" cy="16" r="9" fill="#EB001B"/>
          <circle cx="29" cy="16" r="9" fill="#F79E1B"/>
          <path d="M24 9.2c2.2 1.7 3.6 4.3 3.6 7.3s-1.4 5.6-3.6 7.3c-2.2-1.7-3.6-4.3-3.6-7.3s1.4-5.6 3.6-7.3z" fill="#FF5F00"/>
        </svg>
      </PaymentIcon>

      {/* Maestro */}
      <PaymentIcon name="maestro">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="white" rx="2"/>
          <circle cx="19" cy="16" r="9" fill="#0099DF"/>
          <circle cx="29" cy="16" r="9" fill="#ED0006"/>
          <path d="M24 9.2c2.2 1.7 3.6 4.3 3.6 7.3s-1.4 5.6-3.6 7.3c-2.2-1.7-3.6-4.3-3.6-7.3s1.4-5.6 3.6-7.3z" fill="#6C6BBD"/>
        </svg>
      </PaymentIcon>

      {/* American Express */}
      <PaymentIcon name="amex">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="#006FCF" rx="2"/>
          <text x="24" y="14" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="Arial">AMEX</text>
          <text x="24" y="22" textAnchor="middle" fill="white" fontSize="5" fontFamily="Arial">EXPRESS</text>
        </svg>
      </PaymentIcon>

      {/* Apple Pay */}
      <PaymentIcon name="apple-pay">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="#000" rx="2"/>
          <text x="24" y="19" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" fontFamily="system-ui">Pay</text>
          <path d="M13 11c-.4.5-1.1.9-1.7.8-.1-.7.2-1.4.6-1.8.4-.5 1.1-.8 1.7-.9.1.7-.2 1.4-.6 1.9zm.6.9c-.9-.1-1.7.5-2.2.5-.4 0-1.1-.5-1.8-.5-.9 0-1.8.5-2.3 1.4-1 1.7-.3 4.2.7 5.6.5.7 1 1.4 1.8 1.4.7 0 1-.5 1.8-.5s1.1.5 1.8.5c.8 0 1.2-.7 1.7-1.4.5-.8.7-1.5.7-1.6-.7-.3-1.3-1.1-1.3-2.2 0-.9.5-1.7 1.1-2.2-.6-.8-1.5-1-2-1z" fill="white"/>
        </svg>
      </PaymentIcon>

      {/* Google Pay */}
      <PaymentIcon name="google-pay">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="white" rx="2" stroke="#ddd" strokeWidth="0.5"/>
          <text x="30" y="19" textAnchor="middle" fill="#5F6368" fontSize="9" fontWeight="500" fontFamily="system-ui">Pay</text>
          <g transform="translate(8,10) scale(0.55)">
            <path d="M17.6 10.3V16h-2.2v-14h5.8c1.4 0 2.6.5 3.5 1.4.9.9 1.4 2 1.4 3.3 0 1.3-.5 2.4-1.4 3.3-.9.9-2.1 1.3-3.5 1.3h-3.6z" fill="#5F6368"/>
            <path d="M6.6 16c-2.7 0-5-2.2-5-5s2.3-5 5-5c1.5 0 2.5.6 3.3 1.3l-1.5 1.5c-.5-.5-1.2-.8-1.8-.8-1.6 0-2.8 1.3-2.8 3s1.2 3 2.8 3c1 0 1.6-.4 2-.8.3-.3.5-.8.6-1.4H6.6V8.5h4.3c0 .3.1.6.1 1 0 1.2-.3 2.7-1.4 3.7-.9 1.2-2.1 1.8-3.6 1.8z" fill="#4285F4"/>
          </g>
        </svg>
      </PaymentIcon>

      {/* Klarna */}
      <PaymentIcon name="klarna">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="#FFB3C7" rx="2"/>
          <text x="24" y="19" textAnchor="middle" fill="#0A0A0A" fontSize="10" fontWeight="bold" fontFamily="system-ui">Klarna</text>
        </svg>
      </PaymentIcon>

      {/* PayPal */}
      <PaymentIcon name="paypal">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="white" rx="2" stroke="#ddd" strokeWidth="0.5"/>
          <text x="24" y="18" textAnchor="middle" fontSize="9" fontWeight="bold" fontFamily="system-ui">
            <tspan fill="#003087">Pay</tspan><tspan fill="#009CDE">Pal</tspan>
          </text>
        </svg>
      </PaymentIcon>

      {/* Shop Pay */}
      <PaymentIcon name="shop-pay">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="#5A31F4" rx="2"/>
          <text x="24" y="19" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="system-ui">shop</text>
        </svg>
      </PaymentIcon>

      {/* UnionPay */}
      <PaymentIcon name="unionpay">
        <svg viewBox="0 0 48 32" className="w-10 h-6">
          <rect width="48" height="32" fill="white" rx="2"/>
          <rect x="4" y="4" width="13" height="24" rx="2" fill="#E21836"/>
          <rect x="17" y="4" width="14" height="24" rx="2" fill="#00447C"/>
          <rect x="31" y="4" width="13" height="24" rx="2" fill="#007B84"/>
          <text x="24" y="15" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold" fontFamily="Arial">UnionPay</text>
          <text x="24" y="22" textAnchor="middle" fill="white" fontSize="4" fontFamily="Arial">&#x94F6;&#x8054;</text>
        </svg>
      </PaymentIcon>
    </div>
  );
}

export default function Footer() {
  const { isDark } = useTheme();

  const handleServiceClick = (hash) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${hash}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <footer data-testid="main-footer" className={`border-t pb-28 md:pb-0 transition-colors duration-500 ${isDark ? "bg-[#050505] border-white/5" : "bg-[#1A1A1A] border-black/10"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-4">
            <img src={LOGO_URL} alt="Transfer Avellino" className="h-16 w-auto mb-6" />
            <p className="text-[#A1A1AA] text-sm leading-relaxed max-w-xs">
              Servizio di noleggio con conducente premium ad Avellino.
              Eleganza, puntualità e comfort per ogni vostro spostamento.
            </p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] mb-6">
              Navigazione
            </h4>
            <div className="flex flex-col gap-3">
              {[
                { label: "Home", path: "/" },
                { label: "Chi Siamo", path: "/chi-siamo" },
                { label: "Servizi", path: "/servizi" },
                { label: "Prenota", path: "/prenota" },
                { label: "Contatti", path: "/contatti" },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`footer-link-${link.path.replace("/", "") || "home"}`}
                  className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-300"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Services - now with links */}
          <div className="md:col-span-3">
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] mb-6">
              Servizi
            </h4>
            <div className="flex flex-col gap-3">
              {serviceLinks.map((s) => (
                <Link
                  key={s.label}
                  to={s.path}
                  onClick={() => handleServiceClick(s.hash)}
                  data-testid={`footer-service-${s.hash}`}
                  className="text-sm text-[#A1A1AA] hover:text-[#C9A646] transition-colors duration-300"
                >
                  {s.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] mb-6">
              Contatti
            </h4>
            <div className="flex flex-col gap-4">
              <a
                href="tel:+393784035054"
                data-testid="footer-phone"
                className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 text-[#C9A646]" strokeWidth={1.5} />
                +39 378 4035054
              </a>
              <a
                href="mailto:info@transferavellino.com"
                data-testid="footer-email"
                className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 text-[#C9A646]" strokeWidth={1.5} />
                info@transferavellino.com
              </a>
              <div className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                <MapPin className="w-4 h-4 text-[#C9A646] mt-0.5" strokeWidth={1.5} />
                Avellino, Italia
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="border-t border-white/5 mt-12 pt-8">
          <p className="text-xs font-medium tracking-[0.15em] uppercase text-[#A1A1AA] mb-4">
            Metodi di Pagamento Accettati
          </p>
          <PaymentIcons />
        </div>

        {/* Bottom */}
        <div className="border-t border-white/5 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#A1A1AA]">
            &copy; {new Date().getFullYear()} Transfer Avellino. Tutti i diritti riservati.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://customer-assets.emergentagent.com/job_luxury-ride-avellino/artifacts/favlrkbr_Privacy%20Policy%20Kuruma%20Rent.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="Privacy_Policy_Transfer_Avellino.pdf"
              data-testid="footer-privacy-link"
              className="text-xs text-[#A1A1AA] hover:text-[#C9A646] transition-colors duration-300 underline underline-offset-2"
            >
              Privacy Policy
            </a>
            <button
              onClick={() => {
                const event = new CustomEvent("open-cookie-settings");
                window.dispatchEvent(event);
              }}
              data-testid="footer-cookie-link"
              className="text-xs text-[#A1A1AA] hover:text-[#C9A646] transition-colors duration-300 underline underline-offset-2"
            >
              Cookie Policy
            </button>
            <p className="text-xs text-[#A1A1AA]">
              P.IVA: 03110090648
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
